import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';
import FilterBar from '../components/FilterBar';
import Panel from '../components/Panel';
import NoStrictModeBoundary from '../components/NoStrictModeBoundary';

import { typeLabel } from '../utils/entityLabels';

import WeekNote from '../components/WeekNote';

const SENTIMENT_LABELS = ['positive', 'neutral', 'negative', 'mixed'];
const SENTIMENT_COLORS = { positive: '#1f7a3f', neutral: '#c98b12', negative: '#c2410c', mixed: '#7c3aed' };
// KPI-card mix colours. Positive and negative reuse SENTIMENT_COLORS; neutral is
// deliberately blue here rather than the palette's amber, which reads as a warning on a
// bucket that is really "no strong opinion". The charts below still use the amber, since
// the map's colorscale depends on red -> amber -> green being a diverging ramp.
const MIX_COLORS = {
  positive: SENTIMENT_COLORS.positive,
  negative: SENTIMENT_COLORS.negative,
  neutral: '#1f5eff',
  mixed: SENTIMENT_COLORS.mixed,
};
const MIX_SHORT = { positive: 'pos', negative: 'neg' };

// Leaderboard ranking. "Leaderboard" implies most-liked, not most-talked-about, so rows
// are ranked by net sentiment rather than mention volume. The floor exists because
// ranking on a ratio without one is meaningless: on syngentaind the unfiltered top 10 was
// ten entities with a single 100%-positive mention each. Both numbers are surfaced in the
// panel's description so the board is not silently filtered.
const LEADERBOARD_TOP_N = 10;
const LEADERBOARD_MIN_MENTIONS = 5;
// If fewer than this many entities clear the mention floor, the floor is stepped down (by
// LEADERBOARD_MIN_MENTIONS at a time, to 0) rather than showing a board with one or two
// bars on it. Company hits this on syngentaind: only Syngenta clears 5 mentions, so the
// floor drops to 0 and every company is ranked.
const LEADERBOARD_MIN_BARS = 5;
const labelText = (label) => label.charAt(0).toUpperCase() + label.slice(1);

const paramsFromFilters = (filters) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== '') params.append(k, v); });
  return params;
};

// Display-only relabelling of entity types. Was a hardcoded { Product: 'Brand' } here;
// it now comes from the org config so every page renames the same way and a second org
// can rename differently. The stored entity_type is still the real DB value — it is fed
// into the SQL IN list on /api/sentiment/* and keys the kpis buckets — so it must NOT be
// renamed in the data. See src/utils/entityLabels.js.
const topicLabel = typeLabel;

function KpiCard({ label, value, note, inlineNote = false, accent = 'blue' }) {
  const colors = { blue: '#1f5eff', green: '#1f7a3f', amber: '#c98b12', teal: '#0f766e', red: '#c2410c' };
  // inlineNote sets the note beside the figure instead of under it: at 12px on its own
  // line a four-part sentiment mix was too small to read, and it wrapped.
  if (inlineNote && note) {
    return (
      <div className="metric-card">
        <span className="metric-label">{label}</span>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
          <div className="metric-value" style={{ color: colors[accent] || colors.blue }}>{value}</div>
          <div style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text-light)' }}>{note}</div>
        </div>
      </div>
    );
  }
  return (
    <div className="metric-card">
      <span className="metric-label">{label}</span>
      <div className="metric-value" style={{ color: colors[accent] || colors.blue }}>{value}</div>
      {note && <div style={{ fontSize: '12px', color: 'var(--text-lighter)', marginTop: '6px' }}>{note}</div>}
    </div>
  );
}

/** How much the stored evidence can be trusted, written for a reviewer rather than a
 * developer. Mirrors fact_conversation_entity.evidence_kind (migration 022). An older
 * row predating verification has no value at all, which is why 'unverified' exists as a
 * real state rather than a NULL — "nobody checked this" and "checked, and there is
 * genuinely no single quote" must not look the same on the card. */
const EVIDENCE_KIND = {
  verbatim: {
    label: 'Verified quote',
    note: null,
    noteWithoutQuote: null,
    color: '#1f7a3f',
  },
  general_statement: {
    label: 'No single quote',
    note: 'The farmer never says this in one sentence — the view is spread across the call.',
    noteWithoutQuote: 'The farmer never says this in one sentence — the view is spread across the call.',
    color: '#c98b12',
  },
  unverified: {
    label: 'Not verified',
    note: 'Recorded before quote-checking existed. The quote below has not been matched against the transcript.',
    // The note above promises a quote. On a row that has none it reads as a broken card
    // — it points at something that isn't there — so the no-quote case gets its own
    // sentence. Both states are real: 'unverified' rows predate the quote checker, and
    // some of them never carried a quote to begin with.
    noteWithoutQuote: 'Recorded before quote-checking existed, and no supporting line was stored with it. Nothing here has been matched against the transcript.',
    color: '#c2410c',
  },
};

function QuoteCard({ q }) {
  let quotes = [];
  try {
    const parsed = JSON.parse(q.evidence || '[]');
    quotes = Array.isArray(parsed) ? parsed : [];
  } catch {
    quotes = [];
  }
  const color = SENTIMENT_COLORS[q.sentiment] || 'var(--text-lighter)';
  const kind = EVIDENCE_KIND[q.evidence_kind] || null;
  return (
    <div style={{
      borderLeft: `4px solid ${color}`, background: 'var(--bg-dark)',
      border: '1px solid var(--card-border)', borderRadius: '12px',
      padding: '16px', marginBottom: '12px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
        <span style={{ fontWeight: 700, fontSize: '13px', color: 'var(--text-dark)' }}>
          {q.entity_name} <span style={{ fontWeight: 400, color: 'var(--text-lighter)' }}>({topicLabel(q.entity_type)})</span>
          {/* Grounding renames the row but not the quote, so a card reading "Incipio"
              over a quote saying "NCPO" looks broken. Naming both removes the apparent
              contradiction instead of hiding it. */}
          {q.raw_entity_name && (
            <span style={{ fontWeight: 400, color: 'var(--text-lighter)' }}>
              {' '}— farmer said &ldquo;{q.raw_entity_name}&rdquo;
            </span>
          )}
        </span>
        <span style={{ fontSize: '11px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.1em', color }}>
          {q.sentiment}
        </span>
      </div>
      {q.reasoning && <p style={{ fontSize: '13px', color: 'var(--text-light)', margin: '0 0 8px' }}>{q.reasoning}</p>}

      {kind && (
        <div style={{
          display: 'inline-block', fontSize: '10px', fontWeight: 900, textTransform: 'uppercase',
          letterSpacing: '0.08em', color: kind.color, border: `1px solid ${kind.color}`,
          borderRadius: '999px', padding: '2px 8px', marginBottom: '8px',
        }}>
          {kind.label}
        </div>
      )}

      {quotes.slice(0, 2).map((item, i) => (
        <p key={i} style={{
          fontSize: '13px', fontStyle: 'italic', color: 'var(--text-dark)',
          margin: '4px 0', paddingLeft: '10px', borderLeft: `2px solid ${kind ? kind.color : 'var(--card-border)'}`,
        }}>
          &ldquo;{typeof item === 'string' ? item : item.quote}&rdquo;
        </p>
      ))}

      {/* A general statement has no quote by definition. Say what supports it instead of
          rendering an empty card, which is what made these look like missing data.
          evidence_note is per-row and always preferred — it names the actual gap ("the
          closest line never mentions Incipio", "the line offered does not appear in this
          call") where the kind's own sentence can only speak in general terms. */}
      {quotes.length === 0 && (q.evidence_note || kind?.noteWithoutQuote) && (
        <p style={{
          fontSize: '13px', color: 'var(--text-light)', margin: '4px 0',
          paddingLeft: '10px', borderLeft: `2px dashed ${kind ? kind.color : 'var(--card-border)'}`,
        }}>
          <span style={{ fontWeight: 700 }}>Why there is no quote: </span>
          {q.evidence_note || kind.noteWithoutQuote}
        </p>
      )}
      {quotes.length > 0 && q.evidence_kind === 'unverified' && (
        <p style={{ fontSize: '11px', color: EVIDENCE_KIND.unverified.color, margin: '6px 0 0' }}>
          {EVIDENCE_KIND.unverified.note}
        </p>
      )}

      <div style={{ fontSize: '11px', color: 'var(--text-lighter)', marginTop: '6px' }}>{q.call_date}</div>
    </div>
  );
}

function EntityPicker({ available, selected, onAdd, onRemove, placeholder }) {
  const unselected = available.filter(n => !selected.includes(n));
  return (
    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
      <select
        value=""
        onChange={e => { if (e.target.value) onAdd(e.target.value); }}
        style={{
          padding: '8px 12px', border: '1px solid var(--card-border)', borderRadius: '10px',
          background: 'var(--card-bg)', color: 'var(--text-dark)', fontSize: '13px', fontFamily: 'inherit',
        }}
      >
        <option value="">{placeholder}</option>
        {unselected.map(n => <option key={n} value={n}>{n}</option>)}
      </select>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {selected.length ? selected.map(n => (
          <span key={n} style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '4px 10px', borderRadius: '16px', fontSize: '12px', fontWeight: 600,
            background: 'var(--bg-dark)', border: '1px solid var(--card-border)', color: 'var(--text-dark)',
          }}>
            {n}
            <span
              onClick={() => onRemove(n)}
              style={{ cursor: 'pointer', color: 'var(--text-lighter)', fontWeight: 900, lineHeight: 1 }}
            >
              ×
            </span>
          </span>
        )) : <span style={{ fontSize: '12px', color: 'var(--text-lighter)' }}>All entities shown</span>}
      </div>
    </div>
  );
}

/** How a cluster is described to the reader.
 *
 * `cluster.summary` is the reasoning of ONE member conversation, so it must never be
 * presented as describing the whole cluster — that is what made headings disagree with
 * the quotes underneath them. A multi-member cluster is named by `cluster.label`, the
 * wording its members genuinely share, and falls back to a bare count when they share
 * nothing nameable. A singleton is described by its own summary, which is exact.
 *
 * `label_support` is how many members actually use that wording. It is shown whenever it
 * is less than the whole cluster, because the label is a majority theme rather than a
 * universal one and a heading that hides the difference overstates its own reach — the
 * gap the "e.g." line below already exists to keep honest. */
function clusterHeading(cluster) {
  if (cluster.count === 1) return cluster.summary;
  if (cluster.headline) return cluster.headline;
  if (cluster.label?.length) return cluster.label.join(' · ');
  return `${cluster.count} related comments`;
}

/** The shared wording, shown UNDER a written headline rather than as the heading.
 *
 * The headline is generated and the label is measured, so they answer different
 * questions: what this group says, and how many of them demonstrably say it. Keeping
 * both means a generated line always has the counted evidence sitting beneath it, and a
 * reader can see when a confident-sounding headline rests on a third of the group. When
 * there is no headline the label is promoted to the heading and this returns null, so it
 * never appears twice. */
function clusterSupportNote(cluster) {
  if (cluster.count === 1 || !cluster.label?.length) return null;
  const n = cluster.label_support;
  const wording = cluster.label.join(' · ');
  if (!cluster.headline) {
    // Label is the heading; only the share is worth adding.
    return n && n < cluster.count ? `${n} of ${cluster.count} use this wording` : null;
  }
  return n && n < cluster.count
    ? `${n} of ${cluster.count} say “${wording}”`
    : `all ${cluster.count} say “${wording}”`;
}

/** Ranked list of theme clusters for one sentiment bucket, in the entity drill-down panel. */
function ClusterList({ label, clusters, color, onSelect }) {
  const farmerCount = clusters.reduce((sum, c) => sum + c.count, 0);
  return (
    <div>
      <div style={{ fontSize: '11px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.08em', color, marginBottom: '10px' }}>
        {label}{clusters.length ? ` · ${farmerCount} farmer${farmerCount === 1 ? '' : 's'}` : ''}
      </div>
      {clusters.length === 0 && (
        <div style={{ fontSize: '13px', color: 'var(--text-lighter)', fontStyle: 'italic' }}>No {label.toLowerCase()} mentions</div>
      )}
      {clusters.map((c, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onSelect(c)}
          style={{
            display: 'block', width: '100%', textAlign: 'left', marginBottom: '8px',
            padding: '10px 12px', borderRadius: '10px', border: '1px solid var(--card-border)',
            background: 'var(--bg-dark)', cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
            <span style={{ fontSize: '13px', color: 'var(--text-dark)' }}>{clusterHeading(c)}</span>
            <span style={{ fontSize: '12px', fontWeight: 700, color, whiteSpace: 'nowrap' }}>
              {c.count} farmer{c.count === 1 ? '' : 's'}
            </span>
          </div>
          {clusterSupportNote(c) && (
            <div style={{ fontSize: '11px', color: 'var(--text-lighter)', marginTop: '3px' }}>
              {clusterSupportNote(c)}
            </div>
          )}
          {c.count > 1 && (
            <div style={{ fontSize: '12px', color: 'var(--text-lighter)', marginTop: '4px' }}>
              e.g. &ldquo;{c.summary}&rdquo;
            </div>
          )}
        </button>
      ))}
    </div>
  );
}

/** Level 2 (theme summary) + level 3 (verbatim quotes) drill-down for one entity, opened by
 * clicking a bar in the leaderboard. Summary clusters are computed server-side by
 * utils/sentiment_summary.cluster_reasoning() — independently per sentiment bucket. */
function EntitySentimentPanel({ entityName, entityType, filters, onClose }) {
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [activeCluster, setActiveCluster] = useState(null); // { sentiment, cluster }
  const [quotes, setQuotes] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);

  useEffect(() => {
    setSummaryLoading(true);
    setActiveCluster(null);
    const params = paramsFromFilters(filters);
    params.append('entity_name', entityName);
    axios.get(`/api/sentiment/summary?${params}`)
      .then(res => setSummary(res.data.data || {}))
      .catch(err => console.error('Error loading sentiment summary:', err))
      .finally(() => setSummaryLoading(false));
  }, [entityName, filters]);

  useEffect(() => {
    if (!activeCluster) { setQuotes([]); return; }
    setQuotesLoading(true);
    const params = paramsFromFilters(filters);
    params.append('entity_name', entityName);
    params.append('sentiment', activeCluster.sentiment);
    activeCluster.cluster.conversation_ids.forEach(id => params.append('conversation_id', id));
    axios.get(`/api/sentiment/quotes?${params}`)
      .then(res => setQuotes(res.data.data || []))
      .catch(err => console.error('Error loading quotes:', err))
      .finally(() => setQuotesLoading(false));
  }, [activeCluster, entityName, filters]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px', overflowY: 'auto',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--card-bg)', borderRadius: '16px', padding: '28px',
          maxWidth: '900px', width: '100%', border: '1px solid var(--card-border)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-lighter)' }}>
              {topicLabel(entityType)}
            </div>
            <h3 style={{ margin: '4px 0 0' }}>{entityName}</h3>
          </div>
          <button
            type="button" onClick={onClose} aria-label="Close"
            style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: 'var(--text-lighter)', lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        {activeCluster ? (
          <div>
            <button
              type="button"
              onClick={() => setActiveCluster(null)}
              style={{ background: 'none', border: 'none', color: '#1f5eff', cursor: 'pointer', fontSize: '13px', fontWeight: 600, padding: 0, marginBottom: '16px' }}
            >
              ← Back to summary
            </button>
            {/* The headline is a written description of the group; the shared wording is
                the measured evidence for it. Both are shown, and the caveat stays
                attached either way: these are separate conversations grouped by
                similarity, so the heading describes a tendency across them, not a
                statement any one farmer made. */}
            <p style={{ fontSize: '13px', color: 'var(--text-light)', marginBottom: '16px' }}>
              {activeCluster.cluster.count === 1
                ? <>Quote behind: &ldquo;{activeCluster.cluster.summary}&rdquo;</>
                : <>
                    {activeCluster.cluster.headline
                      ? <><strong>{activeCluster.cluster.headline}</strong> — {activeCluster.cluster.count} conversations. </>
                      : <>{activeCluster.cluster.count} related comments. </>}
                    {activeCluster.cluster.label?.length
                      ? <>They share the wording &ldquo;{activeCluster.cluster.label.join(' · ')}&rdquo;
                          {activeCluster.cluster.label_support &&
                           activeCluster.cluster.label_support < activeCluster.cluster.count
                            ? ` (${activeCluster.cluster.label_support} of ${activeCluster.cluster.count})`
                            : ''}. </>
                      : null}
                    Each quote below is its own conversation.
                  </>}
            </p>
            {quotesLoading ? (
              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-lighter)' }}>Loading…</div>
            ) : quotes.length ? (
              quotes.map((q, i) => <QuoteCard key={`${q.conversation_id}-${i}`} q={q} />)
            ) : (
              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-lighter)' }}>No quotes found for this theme</div>
            )}
          </div>
        ) : summaryLoading ? (
          <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-lighter)' }}>Loading…</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '24px' }}>
            <ClusterList
              label="Positive" clusters={summary?.positive || []} color={SENTIMENT_COLORS.positive}
              onSelect={c => setActiveCluster({ sentiment: 'positive', cluster: c })}
            />
            <ClusterList
              label="Neutral" clusters={summary?.neutral || []} color={SENTIMENT_COLORS.neutral}
              onSelect={c => setActiveCluster({ sentiment: 'neutral', cluster: c })}
            />
            <ClusterList
              label="Negative" clusters={summary?.negative || []} color={SENTIMENT_COLORS.negative}
              onSelect={c => setActiveCluster({ sentiment: 'negative', cluster: c })}
            />
            {summary?.mixed?.length > 0 && (
              <ClusterList
                label="Mixed" clusters={summary.mixed} color={SENTIMENT_COLORS.mixed}
                onSelect={c => setActiveCluster({ sentiment: 'mixed', cluster: c })}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Build one stacked-bar trace per sentiment label from flat {[nameKey], sentiment, count} rows.
 *  orientation 'h' runs the bars left-to-right with the categories on the y axis; 'v' (default)
 *  keeps the original vertical columns. */
function stackedTraces(rows, nameKey, orientation = 'v') {
  const lookup = {};
  rows.forEach(r => {
    const key = `${r[nameKey]}|${r.sentiment}`;
    lookup[key] = (lookup[key] || 0) + r.count;
  });
  const total = n => SENTIMENT_LABELS.reduce((s, l) => s + (lookup[`${n}|${l}`] || 0), 0);

  let names = [...new Set(rows.map(r => r[nameKey]))];
  if (orientation === 'h') {
    // Plotly draws the first y category at the bottom, so sorting ascending puts the
    // biggest bar at the top — which is what "leaderboard" implies.
    names = names.sort((a, b) => total(a) - total(b));
  }

  return SENTIMENT_LABELS.map(label => {
    const counts = names.map(n => lookup[`${n}|${label}`] || 0);
    return {
      ...(orientation === 'h'
        ? { x: counts, y: names, orientation: 'h' }
        : { x: names, y: counts }),
      name: labelText(label),
      type: 'bar',
      marker: { color: SENTIMENT_COLORS[label] },
    };
  });
}

/** Weekly stacked-bar trace per sentiment label, summed across entity types.
 *
 * Bars, not a stacked area: in a stack only the bottom band can be read against the axis,
 * and this was the only stacked area on a page where every other sentiment breakdown
 * (Sentiment by Region, the Leaderboard) is stacked bars. Bar height is still the weekly
 * total, so no volume information is lost by the switch — only the "Relative %" mode
 * drops it, which is why that is a toggle rather than the default.
 *
 * Kept separate from stackedTraces() because weeks must be in chronological order, and
 * that helper orders categories by first appearance. */
function trendTraces(rows, entityType = '', entityName = '') {
  // The API returns one row per (week, entity_type, entity_name, sentiment) and this
  // summed straight across all of them, so Company and Product were always merged.
  // entityType and entityName narrow it; '' on either means "no restriction".
  // Row entity_type is upper-cased by the query; the configured values are not.
  const wanted = entityType.toUpperCase();
  const scoped = rows.filter(r =>
    (!wanted || (r.entity_type || '').toUpperCase() === wanted)
    && (!entityName || r.entity_name === entityName));
  const weeks = [...new Set(scoped.map(r => r.week_start))].sort();
  const lookup = {};
  scoped.forEach(r => {
    const key = `${r.week_start}|${r.sentiment}`;
    lookup[key] = (lookup[key] || 0) + r.count;
  });
  return SENTIMENT_LABELS.map(label => ({
    x: weeks,
    y: weeks.map(w => lookup[`${w}|${label}`] || 0),
    name: labelText(label),
    type: 'bar',
    marker: { color: SENTIMENT_COLORS[label] },
  }));
}

/** Pivot flat by-region rows into one row per region with per-label counts + lat/long. */
function pivotByRegion(rows) {
  const byName = {};
  rows.forEach(r => {
    if (!byName[r.region_name]) {
      byName[r.region_name] = {
        region_name: r.region_name, state: r.state, latitude: r.latitude, longitude: r.longitude,
        positive: 0, neutral: 0, negative: 0, mixed: 0, total: 0,
      };
    }
    const bucket = byName[r.region_name];
    // `+=`, not `=`: byRegion carries one row per (region, entity, type, sentiment), so a
    // region with several entities sends several rows for the same sentiment. Assigning
    // kept only the last one while `total` accumulated all of them, leaving the two
    // disagreeing — measured on syngentaind, 67 of 91 regions, with Garut's parts summing
    // to 7 against a total of 110. That skews the map's net sentiment, which is
    // (positive - negative) / total.
    if (SENTIMENT_LABELS.includes(r.sentiment)) bucket[r.sentiment] += r.count;
    bucket.total += r.count;
  });
  return Object.values(byName);
}

/** Group regions into grid cells sized relative to the current visible lon/lat span, so
 * bubbles split apart as the map is zoomed in (smaller visible span -> smaller cells).
 * Always stays a bubble chart (no choropleth) — at high zoom, cells shrink toward one
 * bubble per region since dim_region only stores one lat/long point per resolved region. */
function binRegionsForSpan(regions, spanDeg) {
  const valid = regions.filter(r => r.latitude != null && r.longitude != null && r.total > 0);
  if (!valid.length) return [];
  const cellDeg = Math.max((spanDeg || 40) / 8, 0.05);
  const bins = new Map();
  valid.forEach(r => {
    const key = `${Math.round(r.latitude / cellDeg)}:${Math.round(r.longitude / cellDeg)}`;
    let b = bins.get(key);
    if (!b) {
      b = { latSum: 0, lonSum: 0, positive: 0, negative: 0, neutral: 0, mixed: 0, total: 0, names: [] };
      bins.set(key, b);
    }
    b.latSum += r.latitude * r.total;
    b.lonSum += r.longitude * r.total;
    b.positive += r.positive;
    b.negative += r.negative;
    b.neutral += r.neutral;
    b.mixed += r.mixed;
    b.total += r.total;
    b.names.push(r.region_name);
  });
  return [...bins.values()].map(b => ({
    lat: b.latSum / b.total,
    lon: b.lonSum / b.total,
    total: b.total,
    net: (b.positive - b.negative) / b.total,
    label: b.names.length > 3 ? `${b.names.slice(0, 3).join(', ')} +${b.names.length - 3} more` : b.names.join(', '),
  }));
}

function SentimentIntel({ filters, loading, setLoading }) {
  const [topicTypes, setTopicTypes] = useState([]);
  const [selectedTopicType, setSelectedTopicType] = useState('');
  const [kpis, setKpis] = useState({});
  const [byEntityTypeAll, setByEntityTypeAll] = useState({}); // { TYPE: [{entity_name, sentiment, count}] }
  const [trend, setTrend] = useState([]);
  const [byRegion, setByRegion] = useState([]);
  const [geoView, setGeoView] = useState(null); // { lonRange: [min,max], latRange: [min,max] }
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [appliedMapEntities, setAppliedMapEntities] = useState([]);
  const [trendViewMode, setTrendViewMode] = useState('absolute');   // 'absolute' | 'relative'
  const [trendTopicType, setTrendTopicType] = useState('');         // '' = every configured topic type
  const [trendEntity, setTrendEntity] = useState('');               // '' = all entities of that type
  const [mapTopicType, setMapTopicType] = useState('');             // '' = every topic type on the map
  // Reference map labels live in Azure Blob at {org_container}/config/geo_labels.json,
  // beside the org logo, so an org's geography can change without a redeploy. Fetched
  // once per mount; the endpoint falls back to the bundled config/geo_labels.json.
  const [geoLabelData, setGeoLabelData] = useState({ labels: [], tier2Span: 25 });
  const [leaderViewMode, setLeaderViewMode] = useState('absolute'); // 'absolute' | 'relative'
  const [regionViewMode, setRegionViewMode] = useState('absolute'); // 'absolute' | 'relative'
  // dim_region.region_name is sub-district level — 91 of them carry scored sentiment, which
  // is unreadable as a stacked bar chart. dim_region.state is the province rollup (11 in
  // scored data) and is already in the payload, so this is a client-side regroup. Province
  // is the default so the panel opens legible, with the detail one click away.
  const [regionGranularity, setRegionGranularity] = useState('province'); // 'province' | 'subdistrict'
  const [regionTopicType, setRegionTopicType] = useState('');       // '' = every topic type
  const [regionEntity, setRegionEntity] = useState('');             // '' = all entities of that type
  const [drilldownEntity, setDrilldownEntity] = useState(null); // { name, type } — opens the level-2/3 panel
  const relayoutTimer = useRef(null);

  useEffect(() => () => { if (relayoutTimer.current) clearTimeout(relayoutTimer.current); }, []);

  // Org-configurable list of entity types shown as sentiment "topics" (Field HQ ->
  // Configuration -> Sentiment Topic Entity Types). Fetched once; defaults to
  // Company + Product if unconfigured, matching the backend default.
  useEffect(() => {
    axios.get('/api/config/geo-labels')
      .then(res => setGeoLabelData({
        labels: res.data?.labels || [],
        tier2Span: res.data?.tier2_span ?? 25,
      }))
      .catch(() => setGeoLabelData({ labels: [], tier2Span: 25 }));
  }, []);

  useEffect(() => {
    axios.get('/api/config/sentiment-topic-entity-types')
      .then(res => {
        const types = res.data?.sentiment_topic_entity_types?.length ? res.data.sentiment_topic_entity_types : ['Company', 'Product'];
        setTopicTypes(types);
        setSelectedTopicType(prev => prev || types[0]);
      })
      .catch(() => { setTopicTypes(['Company', 'Product']); setSelectedTopicType(prev => prev || 'Company'); });
  }, []);

  useEffect(() => {
    if (!topicTypes.length) return;
    setLoading(true);
    const params = paramsFromFilters(filters);

    Promise.all([
      axios.get(`/api/sentiment/kpis?${params}`),
      axios.get(`/api/sentiment/trend?${params}`),
      axios.get(`/api/sentiment/by-region?${params}`),
      ...topicTypes.map(t => axios.get(`/api/sentiment/by-entity-type?entity_type=${encodeURIComponent(t)}&${params}`)),
    ])
      .then(([kpiRes, trendRes, regionRes, ...byTypeResArr]) => {
        setKpis(kpiRes.data || {});
        setTrend(trendRes.data.data || []);
        setByRegion(regionRes.data.data || []);
        const merged = {};
        byTypeResArr.forEach((res, i) => { merged[topicTypes[i]] = res.data.data || []; });
        setByEntityTypeAll(merged);
      })
      .catch(err => console.error('Error loading Brand Sentiment:', err))
      .finally(() => setLoading(false));
  }, [filters, setLoading, topicTypes]);

  // Supplied by /api/sentiment/kpis, which already scoped the counts to this org's own
  // company and brand list. Falls back to the plain type label if it is absent.
  const kpiOrgName = kpis.org_name || '';

  const regionRows = useMemo(() => pivotByRegion(byRegion), [byRegion]);

  // Rows for the region bar chart: scoped by type/entity, then tagged with the grouping
  // key for the chosen granularity. The province fallback to region_name mirrors
  // REGION_ROLLUP_SQL in sales_pages.py, so a region with no state still gets a bar
  // instead of collapsing into an "undefined" bucket.
  const regionChartRows = useMemo(() => {
    const wanted = regionTopicType.toUpperCase();
    return byRegion
      .filter(r => (!wanted || (r.entity_type || '').toUpperCase() === wanted)
        && (!regionEntity || r.entity_name === regionEntity))
      .map(r => ({
        ...r,
        group: regionGranularity === 'province'
          ? ((r.state || '').trim() || r.region_name)
          : r.region_name,
      }));
  }, [byRegion, regionTopicType, regionEntity, regionGranularity]);

  // Busiest first. stackedTraces orders categories by first appearance, which here is the
  // query's alphabetical region order — so the biggest bar could land anywhere.
  const regionCategories = useMemo(() => {
    const totals = new Map();
    regionChartRows.forEach(r => totals.set(r.group, (totals.get(r.group) || 0) + r.count));
    return [...totals.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([g]) => g);
  }, [regionChartRows]);

  const regionEntityOptions = useMemo(() => {
    const wanted = regionTopicType.toUpperCase();
    const totals = new Map();
    byRegion.forEach(r => {
      if (wanted && (r.entity_type || '').toUpperCase() !== wanted) return;
      totals.set(r.entity_name, (totals.get(r.entity_name) || 0) + r.count);
    });
    return [...totals.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [byRegion, regionTopicType]);
  const spanDeg = geoView ? Math.max(geoView.lonRange[1] - geoView.lonRange[0], geoView.latRange[1] - geoView.latRange[0]) : null;

  const barLayout = (extra = {}) => ({
    barmode: 'stack',
    plot_bgcolor: 'var(--bg-dark)', paper_bgcolor: 'var(--card-bg)',
    margin: { t: 20, r: 20, b: 60, l: 60 }, height: 320,
    legend: { orientation: 'h', y: -0.25 },
    ...extra,
  });
  const plotConfig = { responsive: true, displaylogo: false, modeBarButtonsToRemove: ['pan2d', 'select2d', 'lasso2d'] };

  // Memoized because the `|| []` fallback would otherwise mint a fresh array identity on
  // every render, defeating the <Plot> memoization described below.
  const leaderboardRows = useMemo(
    () => byEntityTypeAll[selectedTopicType] || [],
    [byEntityTypeAll, selectedTopicType],
  );
  // Memoized so the `data`/`layout`/`onClick` refs handed to react-plotly.js's <Plot>
  // stay stable across unrelated re-renders. Without this, stackedTraces()/barLayout()
  // built fresh literals on every render, so react-plotly.js called Plotly.react() far
  // more than needed — and its internal syncEventHandlers() only (re)binds a click
  // handler when none was previously registered, so once the churn desynced the bound
  // listener from the live DOM node, clicks stopped reaching React entirely (Plotly's
  // own plotly_click still fired — verified directly — the click just never reached
  // this component).
  // Rank by net sentiment = (positive - negative) / scored mentions, the same measure the
  // Sentiment Map uses for bubble colour, so the two panels agree on what "good" means.
  // Rank by net sentiment = (positive - negative) / scored mentions, the same measure the
  // Sentiment Map uses for bubble colour, so the two panels agree on what "good" means.
  // Returns the applied floor alongside the rows: it is not always MIN_MENTIONS, and the
  // panel has to describe the board it actually drew.
  const leaderboardRanked = useMemo(() => {
    const byName = new Map();
    leaderboardRows.forEach(r => {
      const e = byName.get(r.entity_name) || { name: r.entity_name, total: 0, positive: 0, negative: 0 };
      e.total += r.count;
      if (r.sentiment === 'positive') e.positive += r.count;
      if (r.sentiment === 'negative') e.negative += r.count;
      byName.set(r.entity_name, e);
    });
    const all = [...byName.values()].map(e => ({ ...e, net: (e.positive - e.negative) / e.total }));

    // Step the floor down until enough entities qualify to make a board worth showing.
    let floor = LEADERBOARD_MIN_MENTIONS;
    let pool = all.filter(e => e.total >= floor);
    while (floor > 0 && pool.length < LEADERBOARD_MIN_BARS) {
      floor = Math.max(0, floor - LEADERBOARD_MIN_MENTIONS);
      pool = all.filter(e => e.total >= floor);
    }

    const entries = pool
      // total breaks ties so a 6-mention entity cannot outrank a 60-mention one on an
      // identical ratio; name keeps the order stable when both are equal.
      .sort((a, b) => b.net - a.net || b.total - a.total || a.name.localeCompare(b.name))
      .slice(0, LEADERBOARD_TOP_N);
    return { entries, floor };
  }, [leaderboardRows]);

  const leaderboardNames = useMemo(() => leaderboardRanked.entries.map(e => e.name), [leaderboardRanked]);

  const leaderboardData = useMemo(() => {
    const keep = new Set(leaderboardNames);
    return stackedTraces(leaderboardRows.filter(r => keep.has(r.entity_name)), 'entity_name', 'v');
  }, [leaderboardRows, leaderboardNames]);

  // Relative % makes every bar the same height, which erases volume: 5 mentions at 100%
  // positive would look identical to 60 at 100%. The totals line on a second y-axis puts
  // it back. Absolute needs no overlay because bar height already is the total.
  const leaderboardPlotData = useMemo(() => {
    if (leaderViewMode !== 'relative' || !leaderboardData.length) return leaderboardData;
    return [...leaderboardData, {
      x: leaderboardNames,
      y: leaderboardRanked.entries.map(e => e.total),
      yaxis: 'y2',
      type: 'scatter',
      mode: 'lines+markers',
      name: 'Total mentions',
      line: { color: 'var(--text-dark)', width: 1.5 },
      marker: { size: 7, color: 'var(--text-dark)' },
      hovertemplate: '%{x}: %{y} mentions<extra></extra>',
    }];
  }, [leaderboardData, leaderboardNames, leaderboardRanked, leaderViewMode]);

  const leaderboardLayout = useMemo(() => {
    const relative = leaderViewMode === 'relative';
    return barLayout({
      height: 420,
      margin: { t: 40, r: relative ? 60 : 20, b: 120, l: 60 },
      legend: { orientation: 'h', y: 1.02, yanchor: 'bottom', x: 0 },
      barnorm: relative ? 'percent' : undefined,
      // Explicit category order: stackedTraces orders categories by first appearance,
      // which is the query's order, not the ranking's.
      //
      // The mention count rides in the TICK TEXT, not in the category value: the bars' x
      // values stay the bare entity name so handleLeaderboardClick can pass it straight to
      // the drilldown. tickvals are the category indices rather than the names, which is
      // the form Plotly resolves reliably on a categorical axis.
      xaxis: {
        categoryorder: 'array',
        categoryarray: leaderboardNames,
        tickmode: 'array',
        tickvals: leaderboardNames.map((_, i) => i),
        ticktext: leaderboardRanked.entries.map(e => `${e.name} (${e.total})`),
        tickangle: -35,
        automargin: true,
      },
      yaxis: { title: relative ? '% of Mentions' : 'Mentions' },
      ...(relative ? {
        yaxis2: {
          title: 'Total mentions', overlaying: 'y', side: 'right',
          showgrid: false, rangemode: 'tozero', zeroline: false,
        },
      } : {}),
    });
  }, [leaderboardNames, leaderboardRanked, leaderViewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLeaderboardClick = useCallback((e) => {
    const point = e?.points?.[0];
    // Vertical orientation: the entity name is the x value. (It was point.y while these
    // were horizontal bars — changing orientation silently breaks the drilldown if this
    // is missed, because point.y is then a mention count and no panel opens.)
    if (point) setDrilldownEntity({ name: point.x, type: selectedTopicType });
  }, [selectedTopicType]);

  // Options for the trend panel's entity picker: entities present in the trend data for
  // the selected type, strongest first. Derived from the same rows the chart draws, so an
  // option can never resolve to an empty chart.
  const trendEntityOptions = useMemo(() => {
    const wanted = trendTopicType.toUpperCase();
    const totals = new Map();
    trend.forEach(r => {
      if (wanted && (r.entity_type || '').toUpperCase() !== wanted) return;
      totals.set(r.entity_name, (totals.get(r.entity_name) || 0) + r.count);
    });
    return [...totals.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [trend, trendTopicType]);


  const mapRegionRows = useMemo(() => {
    const wanted = mapTopicType.toUpperCase();
    let rows = wanted ? byRegion.filter(r => (r.entity_type || '').toUpperCase() === wanted) : byRegion;
    if (appliedMapEntities.length) rows = rows.filter(r => appliedMapEntities.includes(r.entity_name));
    return pivotByRegion(rows);
  }, [byRegion, appliedMapEntities, mapTopicType]);

  // Entities offered to the map picker, narrowed to the chosen type. Drawn from byRegion
  // rather than byEntityTypeAll so only entities that actually have a mapped location are
  // offered — picking one with no lat/long would blank the map.
  const mapEntityOptions = useMemo(() => {
    const wanted = mapTopicType.toUpperCase();
    const names = new Set();
    byRegion.forEach(r => {
      if (wanted && (r.entity_type || '').toUpperCase() !== wanted) return;
      names.add(r.entity_name);
    });
    return [...names].sort();
  }, [byRegion, mapTopicType]);

  // Reference labels, thinned by zoom: tier 1 (countries, oceans, islands) always, tier 2
  // (provinces, straits) only once zoomed in past the config's tier2_span. Split water from
  // land so the two read differently, drawn as their own traces because Plotly's geo
  // layer has no place-name layer of its own.
  const labelTraces = useMemo(() => {
    if (!geoView || !geoLabelData.labels.length) return [];
    const span = spanDeg == null ? 40 : spanDeg;
    const visible = geoLabelData.labels.filter(l => l.tier === 1 || span < geoLabelData.tier2Span);
    const trace = (kind, color, size, style) => {
      const rows = visible.filter(l => (kind === 'water' ? l.kind === 'water' : l.kind !== 'water'));
      if (!rows.length) return null;
      return {
        type: 'scattergeo', mode: 'text',
        lat: rows.map(l => l.lat), lon: rows.map(l => l.lon),
        text: rows.map(l => l.name),
        textfont: { size, color, style },
        hoverinfo: 'skip', showlegend: false,
      };
    };
    return [
      trace('water', 'var(--text-lighter)', 10, 'italic'),
      trace('land', 'var(--text-light)', 11, 'normal'),
    ].filter(Boolean);
  }, [geoView, spanDeg, geoLabelData]);
  const mapBins = useMemo(() => binRegionsForSpan(mapRegionRows, spanDeg), [mapRegionRows, spanDeg]);

  useEffect(() => {
    if (geoView || !regionRows.length) return;
    const lats = regionRows.map(r => r.latitude).filter(v => v != null);
    const lons = regionRows.map(r => r.longitude).filter(v => v != null);
    if (lats.length) {
      const pad = 1.5;
      setGeoView({
        lonRange: [Math.min(...lons) - pad, Math.max(...lons) + pad],
        latRange: [Math.min(...lats) - pad, Math.max(...lats) + pad],
      });
    }
  }, [regionRows, geoView]);

  return (
    <div>
      <div className="module-header">
        <div style={{ fontSize: '11px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.22em', color: 'var(--text-lighter)', marginBottom: '6px' }}>
          Voice of the Farmer
        </div>
        <h2>Brand Sentiment</h2>
        <p>Farmer sentiment toward Syngenta and competitor companies and products, with regional breakdown and verbatim evidence.</p>
      </div>

      <FilterBar />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        {topicTypes.map(t => {
          const bucket = kpis[t.toLowerCase()] || {};
          const total = bucket.total || 0;
          const pct = n => Math.round((n / total) * 100);
          // The whole mix, not just "positive %". Most Product mentions are neutral, so a
          // bare positive figure reads as bad news when very little is actually negative.
          // Zero buckets are dropped so the note stays short on sparse scopes. Built as
          // nodes rather than a string so each bucket can carry its own colour.
          const present = ['positive', 'negative', 'neutral', 'mixed'].filter(k => (bucket[k] || 0) > 0);
          const mix = (
            <>
              {present.map((k, i) => (
                <React.Fragment key={k}>
                  {i > 0 && <span style={{ color: 'var(--text-lighter)' }}> · </span>}
                  <span style={{ color: MIX_COLORS[k] }}>{`${pct(bucket[k])}% ${MIX_SHORT[k] || k}`}</span>
                </React.Fragment>
              ))}
            </>
          );
          return (
            <KpiCard
              key={t}
              label={kpiOrgName
                ? `${kpiOrgName} ${topicLabel(t) === 'Brand' ? 'Brands' : topicLabel(t)} Mentions Scored`
                : `${topicLabel(t)} Mentions Scored`}
              value={total}
              note={total ? mix : 'No scored mentions in this scope'}
              inlineNote={!!total}
              accent="blue"
            />
          );
        })}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
        <Panel
          loading={loading} title="Sentiment Trend"
          subtitle={trendEntity
            ? `Weekly sentiment mix for ${trendEntity}`
            : trendTopicType
              ? `Weekly sentiment mix for ${topicLabel(trendTopicType)}`
              : 'Weekly sentiment mix across all topic types'}
          cardStyle={{ display: 'flex', flexDirection: 'column' }}
          /* Column, not the default row: the body is a flex container so the toggle would
             otherwise sit beside the chart instead of above it. justifyContent keeps the
             vertical centring this panel needs when the grid row is stretched by the
             taller Sentiment Map next to it. */
          bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
        >
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Topic types come from the org config (sentiment_topic_entity_types), the
                same list the KPI cards and the leaderboard use — nothing is hardcoded. */}
            <select
              aria-label="Entity type"
              value={trendTopicType}
              onChange={e => { setTrendTopicType(e.target.value); setTrendEntity(''); }}
              style={{
                padding: '6px 12px', fontSize: '12px', fontWeight: 600, fontFamily: 'inherit',
                border: '1px solid var(--card-border)', borderRadius: '10px',
                background: 'var(--card-bg)', color: 'var(--text-dark)',
              }}
            >
              <option value="">All types</option>
              {topicTypes.map(t => <option key={t} value={t}>{topicLabel(t)}</option>)}
            </select>
            <select
              aria-label="Entity"
              value={trendEntity}
              onChange={e => setTrendEntity(e.target.value)}
              disabled={!trendEntityOptions.length}
              style={{
                padding: '6px 12px', fontSize: '12px', fontWeight: 600, fontFamily: 'inherit',
                border: '1px solid var(--card-border)', borderRadius: '10px',
                background: 'var(--card-bg)', color: 'var(--text-dark)', maxWidth: '220px',
              }}
            >
              <option value="">All entities</option>
              {trendEntityOptions.map(([name, count]) => (
                <option key={name} value={name}>{`${name} \u2014 ${count} scored`}</option>
              ))}
            </select>
            {[['absolute', 'Absolute'], ['relative', 'Relative %']].map(([mode, label]) => (
              <button
                key={mode}
                onClick={() => setTrendViewMode(mode)}
                style={{
                  padding: '6px 14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                  borderRadius: '10px', border: '1px solid var(--card-border)', fontFamily: 'inherit',
                  background: trendViewMode === mode ? 'var(--text-dark)' : 'var(--card-bg)',
                  color: trendViewMode === mode ? 'var(--card-bg)' : 'var(--text-light)',
                }}
              >
                {label}
              </button>
            ))}
          </div>
          {trend.length ? (
            <>
              <Plot
                data={trendTraces(trend, trendTopicType, trendEntity)}
                layout={barLayout({
                  xaxis: { title: 'Week starting' },
                  barnorm: trendViewMode === 'relative' ? 'percent' : undefined,
                  yaxis: { title: trendViewMode === 'relative' ? '% of Mentions' : 'Mentions' },
                  hovermode: 'x unified',
                })}
                config={plotConfig}
                style={{ width: '100%' }}
              />
              <WeekNote />
            </>
          ) : <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-lighter)', width: '100%' }}>No data available</div>}
        </Panel>

        <Panel loading={loading} title="Sentiment Map" subtitle="Bubble size = mention volume, color = net sentiment (red = negative, green = positive). Zoom in to split bubbles into finer regions.">
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <select
              aria-label="Entity type"
              value={mapTopicType}
              onChange={e => {
                // Entities belong to the old type; clearing both the draft and the applied
                // selection stops a stale pick silently filtering the new type to nothing.
                setMapTopicType(e.target.value);
                setSelectedEntities([]);
                setAppliedMapEntities([]);
              }}
              style={{
                padding: '8px 12px', fontSize: '13px', fontWeight: 600, fontFamily: 'inherit',
                border: '1px solid var(--card-border)', borderRadius: '10px',
                background: 'var(--card-bg)', color: 'var(--text-dark)',
              }}
            >
              <option value="">All types</option>
              {topicTypes.map(t => <option key={t} value={t}>{topicLabel(t)}</option>)}
            </select>
            <div style={{ flex: '1 1 auto' }}>
              <EntityPicker
                available={mapEntityOptions}
                selected={selectedEntities}
                onAdd={n => setSelectedEntities(prev => [...prev, n])}
                onRemove={n => setSelectedEntities(prev => prev.filter(x => x !== n))}
                placeholder="+ Filter map to entity..."
              />
            </div>
            <button
              type="button"
              onClick={() => setAppliedMapEntities(selectedEntities)}
              style={{
                padding: '8px 16px', fontSize: '13px', fontWeight: 600, color: '#fff',
                background: '#1f5eff', border: 'none', borderRadius: '10px', fontFamily: 'inherit',
                cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px',
              }}
            >
              <span aria-hidden>↻</span> Refresh
            </button>
          </div>
          {mapBins.length && geoView ? (
            <NoStrictModeBoundary>
            <Plot
              data={[...labelTraces, {
                type: 'scattergeo',
                mode: 'markers',
                lat: mapBins.map(b => b.lat),
                lon: mapBins.map(b => b.lon),
                text: mapBins.map(b => `${b.label}<br>${b.total} mentions<br>Net sentiment: ${Math.round(b.net * 100)}%`),
                hoverinfo: 'text',
                marker: {
                  size: mapBins.map(b => Math.max(10, Math.sqrt(b.total) * 6)),
                  color: mapBins.map(b => b.net),
                  colorscale: [[0, SENTIMENT_COLORS.negative], [0.5, SENTIMENT_COLORS.neutral], [1, SENTIMENT_COLORS.positive]],
                  cmin: -1, cmax: 1,
                  showscale: true,
                  colorbar: {
                    title: { text: 'Net sentiment', font: { size: 10 } },
                    tickvals: [-1, 1], ticktext: ['Negative', 'Positive'],
                    tickfont: { size: 10 }, thickness: 12, len: 0.5, outlinewidth: 0,
                  },
                  line: { width: 1, color: 'var(--card-bg)' },
                },
              }]}
              layout={{
                geo: {
                  scope: 'world',
                  projection: { type: 'mercator' },
                  lonaxis: { range: geoView.lonRange },
                  lataxis: { range: geoView.latRange },
                  showland: true, landcolor: 'var(--bg-dark)',
                  showcountries: true, countrycolor: 'var(--card-border)',
                  showocean: true, oceancolor: 'var(--card-bg)',
                  showframe: false, resolution: 50,
                },
                margin: { t: 10, r: 10, b: 10, l: 10 }, height: 440,
              }}
              config={plotConfig}
              onRelayout={(e) => {
                const lon0 = e['geo.lonaxis.range[0]'], lon1 = e['geo.lonaxis.range[1]'];
                const lat0 = e['geo.lataxis.range[0]'], lat1 = e['geo.lataxis.range[1]'];
                if (lon0 == null || lon1 == null || lat0 == null || lat1 == null) return;
                // Debounced: feeding a new `layout` prop back into Plotly synchronously,
                // while Plotly's own drag/zoom gesture on this geo subplot is still in
                // progress, re-enters its interaction setup mid-gesture and corrupts its
                // internal state. Wait for the gesture to settle before re-rendering.
                if (relayoutTimer.current) clearTimeout(relayoutTimer.current);
                relayoutTimer.current = setTimeout(() => {
                  setGeoView({ lonRange: [lon0, lon1], latRange: [lat0, lat1] });
                }, 400);
              }}
              style={{ width: '100%' }}
            />
            </NoStrictModeBoundary>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-lighter)' }}>
              {appliedMapEntities.length ? 'No geocoded mentions for the selected entities' : 'No geocoded region data available yet'}
            </div>
          )}
        </Panel>
      </div>

      <div style={{ marginBottom: '32px' }}>
        <Panel
          loading={loading}
          title="Sentiment Leaderboard"
          subtitle={leaderboardRanked.floor > 0
            ? `Top ${LEADERBOARD_TOP_N} by net sentiment, best on the left — among entities with ${leaderboardRanked.floor}+ scored mentions`
            : `Top ${LEADERBOARD_TOP_N} by net sentiment, best on the left — across all scored entities`}
        >
          <p style={{ fontSize: '12px', color: 'var(--text-lighter)', margin: '0 0 12px' }}>
            <strong>Net sentiment</strong> = (positive - negative) / total scored mentions.{' '}
            {leaderboardRanked.floor > 0 ? (
              <>
                Entities below {leaderboardRanked.floor} mentions are excluded: a single
                positive mention would otherwise top the board at 100%.
              </>
            ) : (
              <>
                Too few entities reached {LEADERBOARD_MIN_MENTIONS} mentions here, so every
                scored entity is ranked — read the low-mention entries with care, since one
                positive mention scores 100%.
              </>
            )}{' '}
            Click a bar to see what farmers are saying; click a sentiment in the legend to
            show or hide it.
          </p>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
            {topicTypes.length > 1 && (
              <select
                aria-label="Entity type"
                value={selectedTopicType}
                onChange={e => setSelectedTopicType(e.target.value)}
                style={{
                  padding: '6px 12px', border: '1px solid var(--card-border)', borderRadius: '10px',
                  background: 'var(--card-bg)', color: 'var(--text-dark)', fontSize: '12px',
                  fontWeight: 600, fontFamily: 'inherit',
                }}
              >
                {topicTypes.map(t => <option key={t} value={t}>{topicLabel(t)}</option>)}
              </select>
            )}
            {[['absolute', 'Absolute'], ['relative', 'Relative %']].map(([mode, label]) => (
              <button
                key={mode}
                onClick={() => setLeaderViewMode(mode)}
                style={{
                  padding: '6px 14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                  borderRadius: '10px', border: '1px solid var(--card-border)', fontFamily: 'inherit',
                  background: leaderViewMode === mode ? 'var(--text-dark)' : 'var(--card-bg)',
                  color: leaderViewMode === mode ? 'var(--card-bg)' : 'var(--text-light)',
                }}
              >
                {label}
              </button>
            ))}
          </div>
          {leaderboardRows.length ? (
            // NoStrictModeBoundary: react-plotly.js's onClick binding doesn't survive
            // StrictMode's double mount->unmount->mount (Plotly's own plotly_click still
            // fires on the surviving DOM node; React's bound handler just never receives
            // it — see the component's own doc comment for the mechanism). Same fix
            // already used above for the geo map.
            <NoStrictModeBoundary>
              <Plot
                data={leaderboardPlotData}
                layout={leaderboardLayout}
                config={plotConfig}
                style={{ width: '100%' }}
                onClick={handleLeaderboardClick}
              />
            </NoStrictModeBoundary>
          ) : <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-lighter)' }}>No data available</div>}
        </Panel>
      </div>

      <div style={{ marginBottom: '32px' }}>
        <Panel
          loading={loading}
          title="Sentiment by Region"
          subtitle={`Sentiment mix per ${regionGranularity === 'province' ? 'province' : 'sub-district'}, busiest first \u00b7 counts are scored mentions, so they run lower than the conversation counts in Field Intelligence`}
        >
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              aria-label="Entity type"
              value={regionTopicType}
              onChange={e => { setRegionTopicType(e.target.value); setRegionEntity(''); }}
              style={{
                padding: '6px 12px', fontSize: '12px', fontWeight: 600, fontFamily: 'inherit',
                border: '1px solid var(--card-border)', borderRadius: '10px',
                background: 'var(--card-bg)', color: 'var(--text-dark)',
              }}
            >
              <option value="">All types</option>
              {topicTypes.map(t => <option key={t} value={t}>{topicLabel(t)}</option>)}
            </select>
            <select
              aria-label="Entity"
              value={regionEntity}
              onChange={e => setRegionEntity(e.target.value)}
              disabled={!regionEntityOptions.length}
              style={{
                padding: '6px 12px', fontSize: '12px', fontWeight: 600, fontFamily: 'inherit',
                border: '1px solid var(--card-border)', borderRadius: '10px',
                background: 'var(--card-bg)', color: 'var(--text-dark)', maxWidth: '220px',
              }}
            >
              <option value="">All entities</option>
              {regionEntityOptions.map(([name, count]) => (
                <option key={name} value={name}>{`${name} \u2014 ${count} scored`}</option>
              ))}
            </select>
            {[['province', 'Province'], ['subdistrict', 'Sub-district']].map(([mode, label]) => (
              <button
                key={mode}
                onClick={() => setRegionGranularity(mode)}
                style={{
                  padding: '6px 14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                  borderRadius: '10px', border: '1px solid var(--card-border)', fontFamily: 'inherit',
                  background: regionGranularity === mode ? 'var(--text-dark)' : 'var(--card-bg)',
                  color: regionGranularity === mode ? 'var(--card-bg)' : 'var(--text-light)',
                }}
              >
                {label}
              </button>
            ))}
            {[['absolute', 'Absolute'], ['relative', 'Relative %']].map(([mode, label]) => (
              <button
                key={mode}
                onClick={() => setRegionViewMode(mode)}
                style={{
                  padding: '6px 14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                  borderRadius: '10px', border: '1px solid var(--card-border)', fontFamily: 'inherit',
                  background: regionViewMode === mode ? 'var(--text-dark)' : 'var(--card-bg)',
                  color: regionViewMode === mode ? 'var(--card-bg)' : 'var(--text-light)',
                }}
              >
                {label}
              </button>
            ))}
          </div>
          {regionCategories.length ? (
            <Plot
              data={stackedTraces(regionChartRows, 'group')}
              layout={barLayout({
                height: 400,
                margin: { t: 20, r: 20, b: 130, l: 60 },
                barnorm: regionViewMode === 'relative' ? 'percent' : undefined,
                xaxis: {
                  categoryorder: 'array', categoryarray: regionCategories,
                  tickangle: -35, automargin: true,
                },
                yaxis: { title: regionViewMode === 'relative' ? '% of Mentions' : 'Mentions' },
              })}
              config={plotConfig}
              style={{ width: '100%' }}
            />
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-lighter)' }}>
              {regionEntity || regionTopicType ? 'No regional data for this selection.' : 'No region data available'}
            </div>
          )}
        </Panel>
      </div>

      {drilldownEntity && (
        <EntitySentimentPanel
          entityName={drilldownEntity.name}
          entityType={drilldownEntity.type}
          filters={filters}
          onClose={() => setDrilldownEntity(null)}
        />
      )}
    </div>
  );
}

export default SentimentIntel;
